import React, { useState } from 'react';

import { Box, HStack, Header, Link, Text, addWithConfig } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { IDeliveryDriver } from '@rbi-ctg/menu';
import ActionButton, { ActionButtonVariants } from 'components/action-button';
import CloseButton from 'components/close-button';
import CourierMarkerSvg from 'pages/order-confirmation/delivery/icons/courier-image';
import { primitive } from 'styles/constants/primitives';

const Main = HStack.withConfig({
  marginBottom: '$3.5',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const Heading = Header.withConfig({
  variant: 'headerFour',
  color: Styles.color.black,
  fontSize: 'xl',
  lineHeight: 'md',
  margin: 0,
  marginBottom: '$4',
  textTransform: 'capitalize',
});

const CourierInfoContainer = HStack.withConfig({});

const CourierImg = Box.withConfig({
  alignItems: 'center',
  marginRight: '$2',
  borderRadius: 'full',
});

const CourierInfo = Box.withConfig({
  justifyContent: 'center',
  _text: {
    color: Styles.color.grey.one,
  },
});

const CourierName = Text.withConfig({
  fontSize: 'sm',
  marginRight: '$2',
  isTruncated: true,
  maxWidth: {
    md: '44px',
    base: '$20',
  },
});

const CloseButtonWrapper = Box.withConfig({
  width: '$12',
  height: '$12',
  alignItems: 'center',
  _web: {
    borderRadius: '50%',
    safeAreaY: 0,
  },
});

// TODO: uncomment once make and model become available
// const VehicleInfo = Text.withConfig({
//   fontSize: 'xs',
// });

const ContactCourier = Box;

const CourierContactInfoContainer = HStack.withConfig({
  justifyContent: 'space-between',
  borderTopWidth: 1,
  borderTopColor: primitive.$blackOpacity10,
  paddingTop: '$2',
});

const CourierContactInfo = Box;

const ContactPrompt = Text.withConfig({
  fontSize: 'xs',
  color: Styles.color.grey.one,
});

const ContactInfo = Link.withConfig({
  fontSize: 'sm',
  color: Styles.color.grey.one,
  isUnderlined: false,
});

const ContactButton = addWithConfig(ActionButton).withConfig({
  maxWidth: '$32',
});

interface ICourierDetailProps {
  driver: IDeliveryDriver;
}

const CourierDetail: React.FC<React.PropsWithChildren<ICourierDetailProps>> = ({ driver }) => {
  const [isContactVisible, setIsContactVisible] = useState<boolean>(false);
  const { formatMessage } = useIntl();

  return (
    <>
      <Heading>{formatMessage({ id: 'yourCourier' })}</Heading>
      <Main>
        <CourierInfoContainer>
          <CourierImg testID="driver-img">
            {/* TODO: RN - Figure out how we want to handle image url sources not from sanity */}
            {/* <Picture image={driver?.profileImageUrl} alt="courier" /> */}
            <CourierMarkerSvg />
          </CourierImg>
          <CourierInfo>
            <CourierName testID="driver-name" data-private>
              {driver?.name || formatMessage({ id: 'noCourierName' })}
            </CourierName>
            {/* {TODO: uncomment this line once make and model become available} */}
            {/* <VehicleInfo>Make & Model</VehicleInfo> */}
          </CourierInfo>
        </CourierInfoContainer>
        <ContactCourier>
          <ContactButton
            testID="driver-contact"
            variant={ActionButtonVariants.OUTLINE}
            fullWidth
            disabled={isContactVisible}
            onPress={() => setIsContactVisible(true)}
          >
            {formatMessage({ id: 'contact' })}
          </ContactButton>
        </ContactCourier>
      </Main>
      {isContactVisible && (
        <CourierContactInfoContainer>
          <CourierContactInfo>
            <ContactPrompt>{formatMessage({ id: 'contactCourier' })}</ContactPrompt>
            <ContactInfo href={`tel:${driver?.phoneNumber}`} testID="driver-phone" data-private>
              {driver?.phoneNumber || formatMessage({ id: 'noCourierPhone' })}
            </ContactInfo>
          </CourierContactInfo>
          <CloseButtonWrapper>
            <CloseButton onPress={() => setIsContactVisible(false)} testID="driver-contact-close" />
          </CloseButtonWrapper>
        </CourierContactInfoContainer>
      )}
    </>
  );
};

export default CourierDetail;
